package com.patronat.top

import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import io.github.aakira.napier.DebugAntilog
import io.github.aakira.napier.Napier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.HTMLCanvasElement
import kotlin.math.ceil

@OptIn(ExperimentalComposeUiApi::class)
fun main() {

    Napier.base(DebugAntilog())
    installGlobalErrorLogging()
    val body = document.body ?: return

    // Стилі тіла
    body.style.setProperty("margin", "0")
    body.style.setProperty("padding", "0")
    body.style.setProperty("width", "100%")
    body.style.setProperty("height", "100%")
    body.style.setProperty("overflow", "hidden")

    ComposeViewport(viewportContainer = body) {

        // --- ЛОГІКА ДЛЯ WINDOWS 125% / 150% ---

        // Стан для тригера перемальовки
        var dimensions by remember { mutableStateOf(window.innerWidth to window.innerHeight) }

        LaunchedEffect(Unit) {
            // 1. Слухаємо resize (швидка реакція)
            window.addEventListener("resize", {
                dimensions = window.innerWidth to window.innerHeight
            })

            // 2. Слухаємо scroll (іноді браузер змінює розміри при скролі)
            window.addEventListener("scroll", {
                dimensions = window.innerWidth to window.innerHeight
            })

            // 3. "Heartbeat" (Пульс) - перевірка кожну секунду.
            // Це критично для Windows, бо іноді Dpr змінюється із затримкою після перетягування вікна.
            while (true) {
                val canvas = findComposeCanvas()
                if (canvas != null) {
                    fixCanvasResolution(canvas)
                }
                delay(1000)
            }
        }

        // Реагуємо на зміну розмірів миттєво
        LaunchedEffect(dimensions) {
            val canvas = findComposeCanvas()
            if (canvas != null) {
                fixCanvasResolution(canvas)
            }
        }

        App()
    }
}

private var isGlobalErrorLoggingInstalled = false

private fun installGlobalErrorLogging() {
    if (isGlobalErrorLoggingInstalled) return
    isGlobalErrorLoggingInstalled = true

    window.addEventListener("error", { event ->
        val jsEvent = event.asDynamic()
        val message = jsEvent.message?.toString() ?: "unknown"
        val source = jsEvent.filename?.toString() ?: "unknown"
        val line = jsEvent.lineno?.toString() ?: "?"
        val column = jsEvent.colno?.toString() ?: "?"
        println("[WebError] $message at $source:$line:$column")

        val error = jsEvent.error
        if (error != null) {
            val name = error.name?.toString() ?: "Error"
            val stack = error.stack?.toString()
            println("[WebError] name=$name")
            if (!stack.isNullOrBlank()) {
                println("[WebError] stack=$stack")
            }
        }
    })

    window.addEventListener("unhandledrejection", { event ->
        val jsEvent = event.asDynamic()
        val reason = jsEvent.reason
        println("[UnhandledRejection] reason=${reason?.toString() ?: "unknown"}")
        val stack = reason?.stack?.toString()
        if (!stack.isNullOrBlank()) {
            println("[UnhandledRejection] stack=$stack")
        }
    })
}

fun fixCanvasResolution(canvas: HTMLCanvasElement) {
    val dpr = window.devicePixelRatio

    // Отримуємо поточні CSS-розміри
    val rect = canvas.getBoundingClientRect()
    val logicalWidth = rect.width
    val logicalHeight = rect.height

    // ВАЖЛИВО: Використовуємо ceil (округлення вгору), а не toInt (відкидання).
    // Якщо виходить 1251.25 пікселів, ми маємо створити 1252 пікселя,
    // інакше браузер почне "милити" картинку.
    val physicalWidth = ceil(logicalWidth * dpr).toInt()
    val physicalHeight = ceil(logicalHeight * dpr).toInt()

    // Перевіряємо, чи треба оновлювати, щоб не навантажувати процесор дарма
    if (physicalWidth > 0 && physicalHeight > 0 &&
        (canvas.width != physicalWidth || canvas.height != physicalHeight)) {

        canvas.width = physicalWidth
        canvas.height = physicalHeight

        // Примусово фіксуємо CSS розмір, щоб канвас не "стрибав"
        canvas.style.setProperty("width", "${logicalWidth}px")
        canvas.style.setProperty("height", "${logicalHeight}px")
    }
}

private fun findComposeCanvas(): HTMLCanvasElement? {
    val canvases = document.getElementsByTagName("canvas")
    var bestCanvas: HTMLCanvasElement? = null
    var maxArea = -1.0

    for (index in 0 until canvases.length) {
        val canvas = canvases.item(index) as? HTMLCanvasElement ?: continue
        if (canvas.classList.contains("maplibregl-canvas")) {
            continue
        }

        val rect = canvas.getBoundingClientRect()
        val area = rect.width * rect.height
        if (area > maxArea) {
            maxArea = area
            bestCanvas = canvas
        }
    }

    return bestCanvas
}
